import { cn } from "@/lib/utils";
import { Input } from "@groot/ui/input";
import { Textarea } from "@groot/ui/textarea";
import { useEffect, useRef, useState } from "react";

interface InlineEditableCellProps {
  value: string | number;
  onSave: (value: string | number) => void;
  type?: "text" | "number" | "textarea" | "date";
  className?: string;
  displayFormatter?: (value: string | number) => React.ReactNode;
  placeholder?: string;
  focused?: boolean;
  onFocus?: () => void;
  onBlurComplete?: () => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  expandOnEdit?: boolean; // Like Excel - input expands beyond cell boundaries
}

export function InlineEditableCell({
  value,
  onSave,
  type = "text",
  className,
  displayFormatter,
  placeholder,
  focused = false,
  onFocus,
  onBlurComplete,
  onKeyDown: externalKeyDown,
  expandOnEdit = false,
}: InlineEditableCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  // Auto-focus when focused prop changes to true
  useEffect(() => {
    if (focused && !isEditing) {
      setIsEditing(true);
    }
  }, [focused, isEditing]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (type !== "textarea" && type !== "date") {
        inputRef.current.select();
      }
    }
  }, [isEditing, type]);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  const handleSave = () => {
    if (editValue !== value) {
      onSave(editValue);
    }
    setIsEditing(false);
    onBlurComplete?.();
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
    onBlurComplete?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Tab and Enter trigger navigation - save first, then let parent handle navigation
    if (e.key === "Tab") {
      handleSave();
      externalKeyDown?.(e); // Let parent handle Tab navigation
      return;
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSave();
      externalKeyDown?.(e); // Let parent handle Enter navigation
      return;
    }

    if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
      return;
    }

    // Arrow keys should work normally within the input - don't call external handler
    // This allows cursor movement within the input field
  };

  const displayValue = displayFormatter ? displayFormatter(value) : value;

  if (!isEditing) {
    return (
      <div
        className={cn(
          "min-w-0 px-2 py-1 -mx-2 -my-1 rounded cursor-text hover:bg-muted/50 flex items-center min-h-[28px]",
          className,
        )}
        onClick={() => {
          setIsEditing(true);
          onFocus?.();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsEditing(true);
            onFocus?.();
          }
        }}
      >
        <span className="min-w-0 truncate text-sm">
          {displayValue || <span className="text-muted-foreground">{placeholder || ""}</span>}
        </span>
      </div>
    );
  }

  // Seamless edit (Actual-style): an invisible ghost preserves the exact
  // display metrics so the row never jumps; the editor overlays the cell and
  // stretches across it (including cell padding) like Actual's cell editor.
  return (
    <div className="relative">
      <div
        className="px-2 py-1 -mx-2 -my-1 flex items-center min-h-[28px] invisible"
        aria-hidden="true"
      >
        <span className="text-sm">{displayValue || placeholder || ""}</span>
      </div>
      {type === "textarea" ? (
        <Textarea
          ref={inputRef as React.RefObject<HTMLTextAreaElement>}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          className="absolute -inset-x-2 -inset-y-1 z-50 min-h-[28px] resize-none text-sm border-primary bg-background"
          placeholder={placeholder}
        />
      ) : (
        <Input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type={type}
          value={editValue}
          onChange={(e) =>
            setEditValue(type === "number" ? Number(e.target.value) : e.target.value)
          }
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          className={cn(
            // z-50: float above the sticky table header when editing the first row
            "absolute -inset-x-2 -inset-y-1 z-50 px-2 text-sm border-primary bg-background",
            expandOnEdit && type === "text" && "min-w-[300px] max-w-[600px] shadow-lg",
            // Native date controls need ~120px for text + picker icon; expand past
            // the cell while editing so the year never clips (anchored left).
            type === "date" && "shadow-lg",
            className,
          )}
          style={{
            // Stretch between the inset offsets (beats the primitive's h-8/w-full)
            height: "auto",
            ...(expandOnEdit && type === "text"
              ? {
                  width: `${Math.max(300, Math.min(600, String(editValue).length * 8 + 40))}px`,
                }
              : type === "date"
                ? { width: "150px" }
                : { width: "auto" }),
          }}
          placeholder={placeholder}
          step={type === "number" ? "0.01" : undefined}
        />
      )}
    </div>
  );
}
